import api from "../api";

/**
 * Buying mock exams, past papers and packs from inside the student panel.
 *
 * The student never leaves the page: Stripe's card form is mounted inside the
 * panel's own dialog (embedded Checkout), and when it completes Stripe returns to
 * the same page with ?purchase=success&session_id=…, which completePanelPurchase()
 * records straight away rather than waiting for the Stripe webhook (which may not
 * reach a local machine at all).
 */

/**
 * Turn an axios failure into an Error that keeps the server's refusal detail.
 *
 * The API refuses a row with 422 and a `data` block saying why — `already_purchased`
 * or `already_included`, plus the row's own resource_type/resource_id. Flattening the
 * response to a bare message threw that away, so the basket kept the offending row and
 * every retry failed identically. The extra property is additive: existing callers that
 * only read `err.message` are unaffected.
 */
function purchaseError(err) {
  const body = err?.response?.data;
  const error = new Error(body?.message || err?.message || "Could not start payment.");
  error.refusal = body?.data || null;
  error.status = err?.response?.status ?? null;

  return error;
}

/**
 * Open a Checkout Session for one item and hand back what the embedded form needs.
 *
 * Does not navigate anywhere — {@link useConfirmPurchase} mounts the returned
 * secret in a dialog.
 *
 * @param {"mock_exam"|"paper"|"bundle"} resourceType
 * @param {number} resourceId
 * @param {{immediateSupplyConsent?: boolean}} [options] the buyer's immediate-supply tick (refund policy)
 * @returns {Promise<{clientSecret: string, publishableKey: string, sessionId: string}>}
 * @throws {Error} with the API's message when the item can't be bought (already
 *   included by a plan, pack or course, or already owned)
 */
export async function createPanelCheckout(resourceType, resourceId, { immediateSupplyConsent = false } = {}) {
  let data;
  try {
    const res = await api.post("/student/resource-checkout", {
      resource_type: resourceType,
      resource_id: resourceId,
      return_url: window.location.href,
      ui_mode: "embedded",
      immediate_supply_consent: immediateSupplyConsent,
    });
    data = res.data?.data;
  } catch (err) {
    throw purchaseError(err);
  }

  if (!data?.client_secret || !data?.publishable_key) {
    throw new Error("Could not start payment. Please try again.");
  }

  return {
    clientSecret: data.client_secret,
    publishableKey: data.publishable_key,
    sessionId: data.session_id,
  };
}

/**
 * Open one Checkout Session covering a whole panel basket.
 *
 * Same session shape as the single-item call, so the same return handling and the
 * same basket fulfilment grant every line. The server re-resolves each price and
 * refuses the order outright if any one row cannot be bought, naming it — a basket
 * that silently charged for four of five items would be worse than no basket.
 *
 * @param {{resourceType: string, resourceId: number}[]} items
 * @returns {Promise<{clientSecret: string, publishableKey: string, sessionId: string}>}
 * @throws {Error} with the API's message when any row can't be bought
 */
export async function createPanelBasketCheckout(items, { immediateSupplyConsent = false } = {}) {
  let data;
  try {
    const res = await api.post("/student/resource-checkout", {
      items: items.map((item) => ({
        resource_type: item.resourceType,
        resource_id: item.resourceId,
      })),
      return_url: window.location.href,
      ui_mode: "embedded",
      immediate_supply_consent: immediateSupplyConsent,
    });
    data = res.data?.data;
  } catch (err) {
    throw purchaseError(err);
  }

  if (!data?.client_secret || !data?.publishable_key) {
    throw new Error("Could not start payment. Please try again.");
  }

  return {
    clientSecret: data.client_secret,
    publishableKey: data.publishable_key,
    sessionId: data.session_id,
  };
}

/**
 * Finish a purchase when Stripe sends the student back (?purchase=success&session_id=…),
 * and tidy the address bar so a refresh doesn't repeat it.
 *
 * @returns {Promise<"success"|"cancelled"|"pending"|null>} null when this page load
 *   isn't a return from Stripe
 */
export async function completePanelPurchase() {
  const params = new URLSearchParams(window.location.search);
  const outcome = params.get("purchase");
  if (!outcome) return null;

  const sessionId = params.get("session_id");
  params.delete("purchase");
  params.delete("session_id");
  const query = params.toString();
  window.history.replaceState(
    window.history.state,
    document.title,
    `${window.location.pathname}${query ? `?${query}` : ""}`
  );

  if (outcome !== "success" || !sessionId) return "cancelled";

  try {
    await api.post("/student/checkout/fulfill", { session_id: sessionId });
    return "success";
  } catch {
    // Paid but not recorded yet: the Stripe webhook will grant it shortly.
    return "pending";
  }
}

/** Toast text for completePanelPurchase() outcomes. */
export const PANEL_PURCHASE_MESSAGES = {
  success: "Payment complete — it's ready to start.",
  pending: "Payment received. It will appear here in a moment.",
  cancelled: "Payment cancelled. You haven't been charged.",
};
